import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { apiGet, apiPost, apiPut, apiDelete } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import { PageHeader, KpiRow, Card, SectionHeading, SubTabs, Badge } from "../components/dc";
import { Button } from "../components/ui/button";
import { Plus, Trash2, Loader2, Save, Anchor, ArrowLeft } from "lucide-react";

const VIOLET = "#a855f7", CYAN = "#67e0f0", GREEN = "#34d399", RED = "#fb7185", AMBER = "#fbbf24", MUTE = "#9d8fc0";
const gbp0 = (n) => "£" + Math.round(n || 0).toLocaleString("en-GB");
const gbp2 = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct1 = (n) => `${(n || 0) >= 0 ? "+" : ""}${Number(n || 0).toFixed(1)}%`;
const inputStyle = { background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 8, padding: "6px 9px", fontSize: 13 };
const th = { padding: "8px 10px", textAlign: "left", color: MUTE, fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid #2a1c47", whiteSpace: "nowrap" };
const td = { padding: "7px 10px", fontSize: 13, color: "var(--color-text-1)", borderBottom: "1px solid rgba(42,28,71,0.5)" };

const sourceTone = { price_file: { t: "OK", tone: "green" }, alias: { t: "AKA", tone: "cyan" }, miss: { t: "manual", tone: "amber" }, manual: { t: "manual", tone: "amber" } };

export default function BudgetModeller() {
  usePageTitle("Budget Modeller");
  const [tab, setTab] = useState("scenarios");
  const [drivers, setDrivers] = useState([]);
  const [variants, setVariants] = useState([]);
  const [bomLoaded, setBomLoaded] = useState(true);
  const [scenarios, setScenarios] = useState([]);
  const [editing, setEditing] = useState(null); // scenario id | "new" | null
  const [loading, setLoading] = useState(true);

  const loadScenarios = useCallback(async () => {
    const r = await apiGet("/budget/scenarios");
    setScenarios(r?.scenarios || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [d, v] = await Promise.all([apiGet("/budget/drivers"), apiGet("/budget/variants")]);
        setDrivers(d?.drivers || []);
        setVariants(v?.variants || []);
        setBomLoaded(!!v?.loaded);
        await loadScenarios();
      } finally { setLoading(false); }
    })();
  }, [loadScenarios]);

  if (loading) {
    return (<div data-testid="budget-modeller"><PageHeader title="Budget Modeller" subtitle="Budgeted cost of install" /><div style={{ color: MUTE, padding: 40 }}>Loading…</div></div>);
  }

  if (editing) {
    return (
      <ScenarioEditor
        scenarioId={editing === "new" ? null : editing}
        variants={variants} drivers={drivers}
        onBack={() => { setEditing(null); loadScenarios(); }}
      />
    );
  }

  return (
    <div data-testid="budget-modeller">
      <PageHeader title="Budget Modeller"
        subtitle="Budget the cost of install: build a BOM per variant, apply inflation drivers, blend across the portfolio, and track variance against a frozen baseline."
        right={bomLoaded ? <Button className="btn btn-primary" onClick={() => setEditing("new")} style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="new-scenario-btn"><Plus size={15} /> New scenario</Button> : null} />

      {!bomLoaded && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ color: MUTE, fontSize: 13.5, lineHeight: 1.6 }}>
            No platform BOM loaded yet. Upload the aggregated dispatch/BOM export under <strong style={{ color: "var(--color-text-1)" }}>Settings → Platform BOM</strong>, then build a scenario per install variant here.
          </div>
        </Card>
      )}

      {bomLoaded && (
        <>
          <SubTabs tabs={[{ key: "scenarios", label: "Scenarios" }, { key: "portfolio", label: "Portfolio" }]} active={tab} onChange={setTab} style={{ marginBottom: 16 }} />
          {tab === "scenarios" && <ScenarioList scenarios={scenarios} onOpen={setEditing} onNew={() => setEditing("new")} reload={loadScenarios} />}
          {tab === "portfolio" && <Portfolio />}
        </>
      )}
    </div>
  );
}

function ScenarioList({ scenarios, onOpen, onNew, reload }) {
  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this scenario?")) return;
    try { await apiDelete(`/budget/scenarios/${id}`); reload(); }
    catch { toast.error("Could not delete scenario."); }
  };
  if (!scenarios.length) {
    return (<Card><div style={{ color: MUTE, fontSize: 13.5, padding: "8px 0" }}>No scenarios yet. <button onClick={onNew} style={{ color: VIOLET, background: "none", border: "none", cursor: "pointer", font: "inherit", padding: 0, textDecoration: "underline" }}>Build your first scenario</button>: pick an install variant and its BOM auto-fills from the platform data.</div></Card>);
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))", gap: 14 }}>
      {scenarios.map((s) => (
        <Card key={s.id} role="link" tabIndex={0} onKeyDown={e => { if (e.target === e.currentTarget && e.key === "Enter") onOpen(s.id); }} style={{ cursor: "pointer" }} accent={s.uplift >= 0 ? AMBER : GREEN} onClick={() => onOpen(s.id)} data-testid={`scenario-${s.id}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-1)" }}>{s.name}</div>
              <div style={{ fontSize: 12, color: MUTE, marginTop: 2 }}>{s.category || "—"} · {s.variant}</div>
            </div>
            <button data-testid={`scenario-delete-${s.id}`} aria-label={`Delete ${s.name}`} onClick={(e) => del(s.id, e)} title="Delete" style={{ background: "none", border: "none", cursor: "pointer", color: MUTE, padding: 4 }}><Trash2 size={15} /></button>
          </div>
          <div style={{ display: "flex", gap: 18, marginTop: 14, alignItems: "baseline" }}>
            <div><div style={{ fontSize: 11, color: MUTE }}>Today</div><div style={{ fontSize: 18, fontWeight: 700 }}>{gbp0(s.costToday)}</div></div>
            <div><div style={{ fontSize: 11, color: MUTE }}>Budgeted</div><div style={{ fontSize: 18, fontWeight: 700 }}>{gbp0(s.costBudgeted)}</div></div>
            <div><div style={{ fontSize: 11, color: MUTE }}>Uplift</div><div style={{ fontSize: 18, fontWeight: 700, color: s.uplift >= 0 ? AMBER : GREEN }}>{pct1(s.upliftPct)}</div></div>
          </div>
          {s.isBaseline && <div style={{ marginTop: 10 }}><Badge tone="cyan">Baseline set</Badge></div>}
        </Card>
      ))}
    </div>
  );
}

function ScenarioEditor({ scenarioId, variants, drivers, onBack }) {
  const [name, setName] = useState("");
  const [variant, setVariant] = useState("");
  const [category, setCategory] = useState("");
  const [area, setArea] = useState("");
  const [lines, setLines] = useState([]);
  const [id, setId] = useState(scenarioId);
  const [baseline, setBaseline] = useState(null);
  const [variance, setVariance] = useState(null);
  const [busy, setBusy] = useState(false);
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    if (!scenarioId) return;
    (async () => {
      const r = await apiGet(`/budget/scenarios/${scenarioId}`);
      const s = r?.scenario;
      if (s) {
        setName(s.name || ""); setVariant(s.variant || ""); setCategory(s.category || "");
        setArea(s.area || ""); setLines(s.lines || []); setBaseline(s.baseline || null);
        setVariance(r.variance || null);
      }
    })();
  }, [scenarioId]);

  const drvById = useMemo(() => Object.fromEntries((drivers || []).map((d) => [d.id, d])), [drivers]);

  const compute = useMemo(() => {
    let today = 0, budgeted = 0;
    const byDriver = {};
    const rows = (lines || []).map((li) => {
      const qty = Number(li.quantity) || 0;
      const price = Number(li.priceToday) || 0;
      const d = drvById[li.driverId] || { pct: 0, name: li.driverId };
      const ct = price * qty, cb = price * (1 + (d.pct || 0) / 100) * qty;
      today += ct; budgeted += cb;
      byDriver[li.driverId] = byDriver[li.driverId] || { name: d.name, pct: d.pct, uplift: 0 };
      byDriver[li.driverId].uplift += cb - ct;
      return { ...li, _ct: ct, _cb: cb, _uplift: cb - ct, _pct: d.pct };
    });
    rows.sort((a, b) => Math.abs(b._uplift) - Math.abs(a._uplift));
    const uplift = budgeted - today;
    return { rows, today, budgeted, uplift, upliftPct: today ? uplift / today * 100 : 0, byDriver: Object.values(byDriver).sort((a, b) => Math.abs(b.uplift) - Math.abs(a.uplift)) };
  }, [lines, drvById]);

  const onPickVariant = async (v) => {
    setVariant(v);
    if (!v) return;
    setBuilding(true);
    try {
      const built = await apiGet(`/budget/build/${encodeURIComponent(v)}`);
      setLines(built.lines || []);
      setArea(built.area || "");
      setCategory((c) => c || built.category || "");
      if (!name) setName(v);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not build the BOM for that variant."); }
    finally { setBuilding(false); }
  };

  const setLine = (i, key, val) => setLines((ls) => ls.map((l, j) => j === i ? { ...l, [key]: val } : l));

  const save = async () => {
    if (!name.trim()) { toast.error("Give the scenario a name."); return; }
    setBusy(true);
    try {
      const payload = { name, variant, category, area, lines };
      if (id) { await apiPut(`/budget/scenarios/${id}`, payload); }
      else { const r = await apiPost("/budget/scenarios", payload); setId(r?.scenario?.id); }
      toast.success("Scenario saved.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save the scenario."); }
    finally { setBusy(false); }
  };

  const setAsBaseline = async () => {
    if (!id) { toast.error("Save the scenario first."); return; }
    setBusy(true);
    try {
      const r = await apiPost(`/budget/scenarios/${id}/baseline`, {});
      setBaseline(r?.baseline || null); setVariance(r?.variance || null);
      toast.success("Baseline set. Variance now tracks live prices against it.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not set the baseline."); }
    finally { setBusy(false); }
  };

  const catOptions = useMemo(() => {
    const s = new Set(["Solar", "Heat pump", "Other"]);
    variants.forEach((v) => v.category && s.add(v.category));
    return [...s];
  }, [variants]);

  return (
    <div data-testid="scenario-editor">
      <PageHeader title={id ? name || "Scenario" : "New scenario"}
        subtitle="Pick a variant to auto-build its BOM, map each line to an inflation driver, and read the budgeted uplift."
        right={<button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "1px solid #2a1c47", borderRadius: 8, color: "var(--color-text-1)", padding: "7px 12px", cursor: "pointer", fontSize: 13 }}><ArrowLeft size={14} /> Back</button>} />

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 240, flex: "1 1 240px" }}>
            <span style={{ fontSize: 12, color: MUTE }}>Scenario name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} data-testid="scenario-name" />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200 }}>
            <span style={{ fontSize: 12, color: MUTE }}>Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
              <option value="">—</option>
              {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 220 }}>
            <span style={{ fontSize: 12, color: MUTE }}>Install variant</span>
            <select value={variant} onChange={(e) => onPickVariant(e.target.value)} style={inputStyle} data-testid="scenario-variant">
              <option value="">— pick a variant —</option>
              {variants.map((v) => <option key={v.variant} value={v.variant}>{v.variant} ({v.category})</option>)}
            </select>
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <Button className="btn btn-primary" disabled={busy} onClick={save} style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="save-scenario"><Save size={14} /> {busy ? "Saving…" : "Save"}</Button>
            <Button className="btn btn-secondary" disabled={busy || !id} onClick={setAsBaseline} style={{ display: "inline-flex", alignItems: "center", gap: 6 }} title={id ? "Freeze these budgeted prices as the baseline" : "Save first"}><Anchor size={14} /> Set as baseline</Button>
          </div>
        </div>
      </Card>

      <KpiRow items={[
        { label: "Cost today / install", value: gbp2(compute.today) },
        { label: "Budgeted / install", value: gbp2(compute.budgeted) },
        { label: "Uplift / install", value: gbp2(compute.uplift), hint: pct1(compute.upliftPct) },
        { label: "Lines", value: lines.length },
      ]} />

      {building ? <Card style={{ marginTop: 16 }}><div style={{ color: MUTE, display: "flex", gap: 8, alignItems: "center" }}><Loader2 size={15} className="animate-spin" /> Building the BOM…</div></Card> : lines.length > 0 && (
        <Card style={{ marginTop: 16, overflowX: "auto" }}>
          <SectionHeading title="Bill of materials" subtitle="Price and description auto-fill from the latest price file (SKU-alias aware); lines with no price file are flagged for a manual price. Map each line to its inflation driver." />
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 860 }}>
            <thead><tr>
              <th style={th}>Part</th><th style={th}>Description</th><th style={th}>Supplier</th>
              <th style={{ ...th, textAlign: "right" }}>Qty/install</th><th style={{ ...th, textAlign: "right" }}>Price today</th>
              <th style={th}>Source</th><th style={th}>Inflation driver</th><th style={{ ...th, textAlign: "right" }}>Uplift</th>
            </tr></thead>
            <tbody>
              {compute.rows.map((li) => {
                const i = lines.indexOf(lines.find((x) => x.part === li.part && x.supplier === li.supplier));
                const src = sourceTone[li.priceSource] || sourceTone.manual;
                return (
                  <tr key={`${li.supplier}|${li.part}`}>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: 12 }}>{li.part}</td>
                    <td style={{ ...td, maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={li.description}>{li.description}</td>
                    <td style={{ ...td, fontSize: 12, color: MUTE }}>{li.supplier}</td>
                    <td style={{ ...td, textAlign: "right" }}><input type="number" step="0.001" value={li.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} style={{ ...inputStyle, width: 78, textAlign: "right" }} /></td>
                    <td style={{ ...td, textAlign: "right" }}><input type="number" step="0.0001" value={li.priceToday} onChange={(e) => setLine(i, "priceToday", e.target.value)} style={{ ...inputStyle, width: 92, textAlign: "right", borderColor: li.manualPrice ? "#7c5b16" : "#2a1c47" }} /></td>
                    <td style={td}><Badge tone={src.tone}>{src.t}</Badge></td>
                    <td style={td}>
                      <select value={li.driverId} onChange={(e) => setLine(i, "driverId", e.target.value)} style={{ ...inputStyle, minWidth: 170 }}>
                        {drivers.map((d) => <option key={d.id} value={d.id}>{d.name} ({pct1(d.pct)})</option>)}
                      </select>
                    </td>
                    <td style={{ ...td, textAlign: "right", color: li._uplift >= 0 ? AMBER : GREEN, fontWeight: 600 }}>{gbp2(li._uplift)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {compute.byDriver.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <SectionHeading title="What drives the uplift" subtitle="Budgeted uplift per install grouped by inflation driver." />
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead><tr><th style={th}>Driver</th><th style={{ ...th, textAlign: "right" }}>Rate</th><th style={{ ...th, textAlign: "right" }}>Uplift / install</th><th style={{ ...th, textAlign: "right" }}>Share</th></tr></thead>
            <tbody>
              {compute.byDriver.map((d) => (
                <tr key={d.name}>
                  <td style={td}>{d.name}</td>
                  <td style={{ ...td, textAlign: "right" }}>{pct1(d.pct)}</td>
                  <td style={{ ...td, textAlign: "right", color: d.uplift >= 0 ? AMBER : GREEN, fontWeight: 600 }}>{gbp2(d.uplift)}</td>
                  <td style={{ ...td, textAlign: "right", color: MUTE }}>{compute.uplift ? Math.round(d.uplift / compute.uplift * 100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {baseline && variance?.hasBaseline && (
        <Card style={{ marginTop: 16, overflowX: "auto" }} accent={variance.variance >= 0 ? GREEN : RED}>
          <SectionHeading title="Budget vs actual variance" subtitle={`Live price vs the budgeted baseline frozen ${baseline.at ? new Date(baseline.at).toLocaleDateString("en-GB") : ""}. Positive = under budget.`} />
          <KpiRow items={[
            { label: "Budgeted", value: gbp2(variance.budgeted) },
            { label: "Live", value: gbp2(variance.live) },
            { label: "Variance", value: gbp2(variance.variance), hint: pct1(variance.variancePct) },
          ]} />
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 720, marginTop: 12 }}>
            <thead><tr><th style={th}>Part</th><th style={th}>Description</th><th style={{ ...th, textAlign: "right" }}>Budgeted</th><th style={{ ...th, textAlign: "right" }}>Live</th><th style={{ ...th, textAlign: "right" }}>Variance</th></tr></thead>
            <tbody>
              {variance.lines.map((l) => (
                <tr key={`${l.supplier}|${l.part}`}>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: 12 }}>{l.part}</td>
                  <td style={{ ...td, maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={l.description}>{l.description}</td>
                  <td style={{ ...td, textAlign: "right" }}>{gbp2(l.priceBudgeted)}</td>
                  <td style={{ ...td, textAlign: "right", color: l.live ? "var(--color-text-1)" : MUTE }}>{gbp2(l.priceLive)}{!l.live && " *"}</td>
                  <td style={{ ...td, textAlign: "right", color: l.variance >= 0 ? GREEN : RED, fontWeight: 600 }}>{gbp2(l.variance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {variance.lines.some((l) => !l.live) && <div style={{ fontSize: 11.5, color: MUTE, marginTop: 8 }}>* No live price file for this line, so the budgeted price is carried through.</div>}
        </Card>
      )}
    </div>
  );
}

function Portfolio() {
  const [data, setData] = useState(null);
  const [basis, setBasis] = useState("runRate");
  useEffect(() => { apiGet("/budget/portfolio").then(setData).catch(() => setData({ categories: [], scenarios: [], overall: {} })); }, []);
  if (!data) return <Card><div style={{ color: MUTE }}>Loading…</div></Card>;
  if (!data.scenarios?.length) return <Card><div style={{ color: MUTE, fontSize: 13.5 }}>Save at least one scenario, and set per-variant volumes under <strong style={{ color: "var(--color-text-1)" }}>Settings → Budget Volumes</strong>, to see the portfolio blend.</div></Card>;
  const basisLabel = { runRate: "Run rate", operational: "Operational", budget: "Budget" };
  return (
    <>
      <KpiRow items={[
        { label: "Blended uplift / install", value: gbp2(data.overall.avgUplift) },
        { label: "Forecast installs / yr", value: (data.overall.totalVolume || 0).toLocaleString() },
        { label: "Scenarios", value: data.overall.scenarios || 0 },
      ]} />
      <Card style={{ marginTop: 16, overflowX: "auto" }}>
        <SectionHeading title="By category" subtitle="Average uplift per install, blended across the category's scenarios by forecast volume, plus annualised category spend."
          right={
            <div style={{ display: "inline-flex", gap: 2, background: "#0f0a1e", border: "1px solid #2a1c47", borderRadius: 999, padding: 3 }}>
              {["runRate", "operational", "budget"].map((k) => (
                <button key={k} onClick={() => setBasis(k)} style={{ border: "none", cursor: "pointer", borderRadius: 999, padding: "5px 14px", fontSize: 12.5, fontWeight: 600, background: basis === k ? "linear-gradient(90deg,#a855f7,#ec4899)" : "transparent", color: basis === k ? "#fff" : MUTE }}>{basisLabel[k]}</button>
              ))}
            </div>
          } />
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 760 }}>
          <thead><tr>
            <th style={th}>Category</th><th style={{ ...th, textAlign: "right" }}>Avg today</th><th style={{ ...th, textAlign: "right" }}>Avg budgeted</th>
            <th style={{ ...th, textAlign: "right" }}>Uplift</th><th style={{ ...th, textAlign: "right" }}>{basisLabel[basis]} installs</th><th style={{ ...th, textAlign: "right" }}>Annualised spend</th>
          </tr></thead>
          <tbody>
            {data.categories.map((c) => (
              <tr key={c.category}>
                <td style={{ ...td, fontWeight: 600 }}>{c.category}</td>
                <td style={{ ...td, textAlign: "right" }}>{gbp2(c.avgCostToday)}</td>
                <td style={{ ...td, textAlign: "right" }}>{gbp2(c.avgCostBudgeted)}</td>
                <td style={{ ...td, textAlign: "right", color: c.avgUplift >= 0 ? AMBER : GREEN, fontWeight: 600 }}>{gbp2(c.avgUplift)} <span style={{ color: MUTE, fontWeight: 400 }}>({pct1(c.avgUpliftPct)})</span></td>
                <td style={{ ...td, textAlign: "right", color: MUTE }}>{(c.volumes[basis] || 0).toLocaleString()}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{gbp0(c.annualised[basis])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Card style={{ marginTop: 16, overflowX: "auto" }}>
        <SectionHeading title="Scenarios" subtitle="Each saved scenario and its forecast weight." />
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 720 }}>
          <thead><tr><th style={th}>Scenario</th><th style={th}>Category</th><th style={{ ...th, textAlign: "right" }}>Today</th><th style={{ ...th, textAlign: "right" }}>Budgeted</th><th style={{ ...th, textAlign: "right" }}>Uplift</th><th style={{ ...th, textAlign: "right" }}>Volume/yr</th></tr></thead>
          <tbody>
            {data.scenarios.map((s) => (
              <tr key={s.id}>
                <td style={{ ...td, fontWeight: 600 }}>{s.name}</td>
                <td style={{ ...td, color: MUTE }}>{s.category}</td>
                <td style={{ ...td, textAlign: "right" }}>{gbp2(s.costToday)}</td>
                <td style={{ ...td, textAlign: "right" }}>{gbp2(s.costBudgeted)}</td>
                <td style={{ ...td, textAlign: "right", color: s.uplift >= 0 ? AMBER : GREEN, fontWeight: 600 }}>{pct1(s.upliftPct)}</td>
                <td style={{ ...td, textAlign: "right", color: s.volume ? "var(--color-text-1)" : MUTE }}>{s.volume ? s.volume.toLocaleString() : "not set"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}
